<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  metrics: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['refresh']);
const { t } = useI18n();

const statusCounts = computed(
  () => props.metrics?.documents?.status_counts || {}
);
const retryableCount = computed(
  () => props.metrics?.documents?.retryable_count || 0
);
const blockedCount = computed(() => statusCounts.value?.blocked || 0);
const failedCount = computed(() => statusCounts.value?.failed || 0);

const topErrors = computed(() =>
  (Array.isArray(props.metrics?.top_errors)
    ? props.metrics.top_errors
    : []
  ).slice(0, 5)
);

const trend = computed(() =>
  (Array.isArray(props.metrics?.trend) ? props.metrics.trend : []).slice(-24)
);
const trendMax = computed(() =>
  trend.value.reduce(
    (m, b) => Math.max(m, (b.blocked || 0) + (b.failed || 0)),
    0
  )
);
</script>

<template>
  <CardLayout class="mb-5">
    <div class="flex items-start justify-between gap-3">
      <div class="flex flex-col gap-1">
        <div class="text-sm font-medium text-n-slate-12">
          {{ t('AILAB.DOCUMENTS.HEALTH.TITLE') }}
        </div>
        <div class="text-xs text-n-slate-11">
          {{
            t('AILAB.DOCUMENTS.HEALTH.SUBTITLE', {
              hours: metrics?.window_hours || 24,
            })
          }}
        </div>
      </div>
      <Button
        icon="i-lucide-refresh-cw"
        color="slate"
        variant="outline"
        size="sm"
        :label="t('AILAB.DOCUMENTS.HEALTH.REFRESH')"
        :is-loading="loading"
        :disabled="loading"
        @click="emit('refresh')"
      />
    </div>

    <div class="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.DOCUMENTS.HEALTH.RETRYABLE') }}
        </div>
        <div class="text-lg font-medium text-n-slate-12">
          {{ retryableCount }}
        </div>
      </div>
      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.DOCUMENTS.HEALTH.BLOCKED') }}
        </div>
        <div class="text-lg font-medium text-n-slate-12">
          {{ blockedCount }}
        </div>
      </div>
      <div class="p-3 rounded-lg bg-n-alpha-2">
        <div class="text-xs text-n-slate-11">
          {{ t('AILAB.DOCUMENTS.HEALTH.FAILED') }}
        </div>
        <div class="text-lg font-medium text-n-slate-12">{{ failedCount }}</div>
      </div>
    </div>

    <div class="mt-4">
      <div class="text-sm font-medium text-n-slate-12">
        {{ t('AILAB.DOCUMENTS.HEALTH.TREND_TITLE') }}
      </div>
      <div class="mt-2 p-3 rounded-lg bg-n-alpha-2">
        <div v-if="!trend.length" class="text-sm text-n-slate-11">
          {{ t('AILAB.DOCUMENTS.HEALTH.TREND_EMPTY') }}
        </div>
        <div v-else class="flex items-end gap-1 h-12">
          <div
            v-for="b in trend"
            :key="b.bucket"
            class="w-2 rounded-sm"
            :class="
              (b.blocked || 0) + (b.failed || 0) > 0
                ? 'bg-n-ruby-9'
                : 'bg-n-slate-6'
            "
            :style="{
              height:
                trendMax > 0
                  ? `${Math.max(2, Math.round((((b.blocked || 0) + (b.failed || 0)) / trendMax) * 48))}px`
                  : '2px',
            }"
            :title="
              t('AILAB.DOCUMENTS.HEALTH.TREND_TOOLTIP', {
                blocked: b.blocked || 0,
                failed: b.failed || 0,
              })
            "
          />
        </div>
      </div>
    </div>

    <div class="mt-4">
      <div class="text-sm font-medium text-n-slate-12">
        {{ t('AILAB.DOCUMENTS.HEALTH.TOP_ERRORS') }}
      </div>
      <div v-if="!topErrors.length" class="mt-1 text-sm text-n-slate-11">
        {{ t('AILAB.DOCUMENTS.HEALTH.NO_ERRORS') }}
      </div>
      <div v-else class="mt-2 flex flex-col gap-2">
        <div
          v-for="row in topErrors"
          :key="row.error"
          class="flex items-start justify-between gap-3 p-3 rounded-lg bg-n-alpha-2"
        >
          <div class="text-sm text-n-slate-12 line-clamp-2" :title="row.error">
            {{ row.error }}
          </div>
          <div class="text-sm font-medium text-n-slate-12 shrink-0">
            {{ row.count }}
          </div>
        </div>
      </div>
    </div>
  </CardLayout>
</template>
